import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import { DataTable, FilterBar, ConfirmDialog, UserCreateModal } from '../shared';
import { listUsers, updateUserStatus, updateUser, resendCredentials } from '../../services/userManagementService';
import PhysicianScheduleModal from './PhysicianScheduleModal';
import { useAuth } from '../../contexts/AuthContext';
import { tokens } from '../../theme/tokens';
import { getUserStatus, getStatusColor } from '../../utils/statusUtils';
import toast from 'react-hot-toast';
import EmailIcon from '@mui/icons-material/Email';
import Tooltip from '@mui/material/Tooltip';

/**
 * PhysicianManagement Component
 * 
 * Comprehensive physician management interface for clinic admins.
 * Features: list, search, filter, view, edit, activate/deactivate physicians.
 * Multi-tenant with strict tenantId enforcement.
 */
const PhysicianManagement = () => {
  const { profile } = useAuth();
  
  // Table state
  const [physicians, setPhysicians] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  
  // Dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedPhysician, setSelectedPhysician] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(null);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  // TODO: Fetch specialty options from the backend
  const specialties = [];

  // Fetch physicians
  const fetchPhysicians = useCallback(async () => {
    if (!profile?.tenant_id) {
      setError('No tenant context available');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Convert 0-indexed page (for Material-UI) to 1-indexed (for backend API)
      const apiPage = page + 1;
      
      const result = await listUsers({
        tenantId: profile.tenant_id,
        role: 'physician',
        q: searchTerm,
        status: statusFilter,
        page: apiPage,
        pageSize,
        sortBy,
        sortOrder,
      });

      if (result.error) {
        throw result.error;
      }

      setPhysicians(result.data.rows);
      setTotalCount(result.data.total);
    } catch (err) {
      console.error('Error fetching physicians:', err);
      setError('Failed to load physicians. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [profile?.tenant_id, searchTerm, statusFilter, page, pageSize, sortBy, sortOrder]);

  // Load physicians on mount and when dependencies change
  useEffect(() => {
    fetchPhysicians();
  }, [fetchPhysicians]);

  // Table columns
  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (row) => `Dr. ${row.first_name || ''} ${row.last_name || ''}`.trim() || 'N/A',
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
    },
    {
      key: 'phone',
      header: 'Phone',
      width: 150,
      render: (row) => row.phone || 'N/A',
    },
    {
      key: 'specialty',
      header: 'Specialty',
      width: 150,
      render: (row) => row.specialty || 'N/A',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      width: 120,
      render: (row) => {
        const displayStatus = getUserStatus(row.status);
        return (
          <Chip
            label={displayStatus}
            size="small"
            color={getStatusColor(displayStatus)}
          />
        );
      },
    },
    {
      key: 'created_at',
      header: 'Registered',
      sortable: true,
      width: 150,
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
    },
  ];

  // Action handlers
  const handleView = (physician) => {
    setSelectedPhysician(physician);
    setViewDialogOpen(true);
  };

  const handleViewSchedule = (physician) => {
    setSelectedPhysician(physician);
    setScheduleModalOpen(true);
  };

  const handleEdit = (physician) => {
    setSelectedPhysician(physician);
    setEditForm({
      first_name: physician.first_name || '',
      last_name: physician.last_name || '',
      email: physician.email || '',
      phone: physician.phone || '',
    });
    setEditDialogOpen(true);
  };

  const handleDeactivate = (physician) => {
    setSelectedPhysician(physician);
    setConfirmAction('deactivate');
    setConfirmDialogOpen(true);
  };

  const handleActivate = (physician) => {
    setSelectedPhysician(physician);
    setConfirmAction('activate');
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedPhysician || !profile?.tenant_id) return;

    const newStatus = confirmAction === 'activate' ? 'active' : 'inactive';
    
    const result = await updateUserStatus(
      selectedPhysician.id,
      profile.tenant_id,
      newStatus
    );

    if (result.error) {
      toast.error(`Failed to ${confirmAction} physician`);
    } else {
      await fetchPhysicians();
    }

    setConfirmDialogOpen(false);
    setSelectedPhysician(null);
    setConfirmAction(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedPhysician || !profile?.tenant_id) return;

    setLoading(true);
    try {
      const result = await updateUser(
        selectedPhysician.id,
        profile.tenant_id,
        editForm
      );

      if (result.error) {
        throw result.error;
      }

      await fetchPhysicians();
      setEditDialogOpen(false);
      setSelectedPhysician(null);
    } catch (err) {
      console.error('Error updating physician:', err);
      toast.error('Failed to update physician');
    } finally {
      setLoading(false);
    }
  };

  // Handle resend credentials
  const handleResendCredentials = async (userId) => {
    setResendingEmail(userId);
    try {
      const result = await resendCredentials(userId);
      if (result.error) {
        throw result.error;
      }
      // Show password in toast if email might have failed
      if (result.data?.data?.password) {
        toast.success(
          `Credentials email sent! Password: ${result.data.data.password}\n\n⚠️ If email fails, share this password manually.`,
          { duration: 10000 }
        );
      } else {
        toast.success('Credentials email sent successfully!');
      }
    } catch (error) {
      console.error('Error resending credentials:', error);
      toast.error('Failed to resend credentials email');
    } finally {
      setResendingEmail(null);
    }
  };

  // Actions slot for table
  const actionsSlot = (row) => (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
      <Tooltip title="Resend credentials email">
        <IconButton
          size="small"
          onClick={() => handleResendCredentials(row.id)}
          disabled={resendingEmail === row.id}
          color="primary"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.primary}`,
              outlineOffset: 2,
            },
          }}
        >
          <EmailIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        onClick={() => handleViewSchedule(row)}
        aria-label={`View schedule for Dr. ${row.first_name} ${row.last_name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <EventIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => handleView(row)}
        aria-label={`View Dr. ${row.first_name} ${row.last_name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => handleEdit(row)}
        aria-label={`Edit Dr. ${row.first_name} ${row.last_name}`}
        sx={{
          '&:focus-visible': {
            outline: `2px solid ${tokens.color.primary}`,
            outlineOffset: 2,
          },
        }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      {row.status === 'active' ? (
        <IconButton
          size="small"
          onClick={() => handleDeactivate(row)}
          aria-label={`Deactivate Dr. ${row.first_name} ${row.last_name}`}
          color="error"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.danger}`,
              outlineOffset: 2,
            },
          }}
        >
          <PersonOffIcon fontSize="small" />
        </IconButton>
      ) : (
        <IconButton
          size="small"
          onClick={() => handleActivate(row)}
          aria-label={`Activate Dr. ${row.first_name} ${row.last_name}`}
          color="success"
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.success}`,
              outlineOffset: 2,
            },
          }}
        >
          <PersonIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  // Clear filters handler
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSpecialtyFilter('all');
    setPage(0);
  };

  return (
    <Box sx={{ py: 0, px: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25, fontSize: '1.125rem' }}>
            Physician Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            Manage and monitor physician accounts
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
          sx={{
            '&:focus-visible': {
              outline: `2px solid ${tokens.color.primary}`,
              outlineOffset: 2,
            },
          }}
        >
          Add Physician
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Bar */}
      <FilterBar
        value={searchTerm}
        onChange={setSearchTerm}
        onClear={handleClearFilters}
        placeholder="Search by name, email, or specialty..."
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            label="Status"
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Specialty</InputLabel>
          <Select
            value={specialtyFilter}
            onChange={(e) => {
              setSpecialtyFilter(e.target.value);
              setPage(0);
            }}
            label="Specialty"
          >
            <MenuItem value="all">All Specialties</MenuItem>
            {specialties.map((specialty) => (
              <MenuItem key={specialty} value={specialty}>
                {specialty}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FilterBar>

      {/* Stats */}
      <Box sx={{ mb: 1, mt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {totalCount} total physician{totalCount !== 1 ? 's' : ''}
          {statusFilter !== 'all' && ` (filtered by ${statusFilter})`}
        </Typography>
      </Box>

      {/* Data Table */}
      <DataTable
        columns={columns}
        rows={physicians}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        sortBy={sortBy}
        sortDir={sortOrder}
        loading={loading}
        error={error}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(0);
        }}
        onSortChange={(key, dir) => {
          setSortBy(key);
          setSortOrder(dir);
        }}
        getRowId={(row) => row.id}
        actionsSlot={actionsSlot}
        emptyMessage="No physicians found. Try adjusting your filters or add a new physician."
        ariaLabel="physicians table"
      />

      {/* Schedule Modal */}
      {selectedPhysician && (
        <PhysicianScheduleModal
          open={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          physician={selectedPhysician}
        />
      )}

      {/* Create User Modal */}
      <UserCreateModal
        open={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onUserCreated={fetchPhysicians}
        role="physician"
      />

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Physician Details</DialogTitle>
        <DialogContent>
          {selectedPhysician && (
            <Box sx={{ pt: tokens.space.x2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">Dr. {selectedPhysician.first_name} {selectedPhysician.last_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip label={selectedPhysician.status} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedPhysician.email}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedPhysician.phone || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Specialty</Typography>
                  <Typography variant="body1">{selectedPhysician.specialty || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Registered</Typography>
                  <Typography variant="body1">
                    {selectedPhysician.created_at ? new Date(selectedPhysician.created_at).toLocaleString() : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Physician</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: tokens.space.x2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title={confirmAction === 'activate' ? 'Activate Physician' : 'Deactivate Physician'}
        message={
          selectedPhysician
            ? `Are you sure you want to ${confirmAction} Dr. ${selectedPhysician.first_name} ${selectedPhysician.last_name}?`
            : ''
        }
        onConfirm={handleConfirmAction}
        onCancel={() => {
          setConfirmDialogOpen(false);
          setSelectedPhysician(null);
          setConfirmAction(null);
        }}
        confirmText={confirmAction === 'activate' ? 'Activate' : 'Deactivate'}
        severity={confirmAction === 'deactivate' ? 'warning' : 'info'}
      />
    </Box>
  );
};

export default PhysicianManagement;
